/**
 * GAPS (G3, GAPS-AND-PREREQUISITES.md §6) — ONE pane for everything stopping the plan: what the team must
 * provide, plan fixes (the gap agent's cards), agents' questions, and broken connections. Each item is
 * answered IN PLACE — a value (checked by the server, never a secret), a connection (picked from the
 * room's connections; secrets stay there), or a file (uploaded as context for exactly the agents that
 * need it) — or marked Not needed with a reason. Answers become the agents' PRE-REQ context.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { answerNeed, createAsset, createUploadUrl, uploadFileToPresignedUrl } from '@stepflow/ui-shared/services/dataPlaneApi';
import { listConnections } from '@stepflow/ui-shared/services/toolsApi';
import { computeFileSha256 } from '@stepflow/ui-shared/utils/contentHash';

const KIND_LABEL = {
  account: 'Account', credential: 'Access', connector: 'Connection', decision: 'Decision',
  document: 'Document', context: 'Information', question: 'Question', broken_connection: 'Broken connection',
};
const FORMAT_HINT = {
  'account-number-12': '12-digit account number', email: 'email address', url: 'https://…',
  region: 'region code, e.g. us-east-1', domain: 'domain, e.g. example.com',
};
const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors disabled:opacity-50';

/** Which connection creator "Set up a new connection" should open for this need: the type it names, else
 *  ``cred-broker`` for access to an account/environment (the v1 credential path). '' = the Connections list. */
export function connectorToolFor(need) {
  const t = String((need && need.field && need.field.connectorType) || '').trim();
  if (t) return t;                                     // CREATABLE or an MCP id — the catalog resolves both
  return need && (need.kind === 'credential' || need.kind === 'broken_connection') ? 'cred-broker' : '';
}

function Chip({ children, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-600', rose: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200',
    amber: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200', emerald: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  };
  return <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${tones[tone]}`}>{children}</span>;
}

function answerText(n) {
  if (n.status === 'not_needed') return `Not needed — ${n.notNeededReason}`;
  if (n.provideVia === 'connector') return `Connection: ${n.connectorRef}`;
  if (n.provideVia === 'upload') return `File: ${n.uploadKey}`;
  return n.value;
}

function NeedRow({ need, networkId, poolId, onChanged, onOpenConnections, answered = false }) {
  const [mode, setMode] = useState(answered ? '' : 'provide');   // '' | 'provide' | 'not_needed'
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [conn, setConn] = useState('');
  const [conns, setConns] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const field = need.field || {};
  useEffect(() => {
    if (mode !== 'provide' || need.provideVia !== 'connector' || conns) return;
    listConnections({ networkId, poolId }).then((r) => {
      const list = r.connections || [];
      setConns(list);
      // coming back from Connections: exactly one connection of the type this need calls for → pre-select it
      const want = connectorToolFor(need);
      const hits = want ? list.filter((c) => c && c.toolId === want) : [];
      if (hits.length === 1) setConn(want);
    }).catch(() => setConns([]));
  }, [mode, need, conns, networkId, poolId]);
  const send = useCallback(async (body) => {
    setBusy(true);
    setErr('');
    try {
      await answerNeed(networkId, need.needId, body, { pool: poolId });
      setMode('');
      if (onChanged) onChanged();
    } catch (e) {
      setErr((e && e.data && e.data.message) || (e && e.message) || 'could not save');
    } finally {
      setBusy(false);
    }
  }, [networkId, poolId, need.needId, onChanged]);
  const upload = useCallback(async (file) => {
    if (!file) return;
    setBusy(true);
    setErr('');
    try {
      const contentHash = await computeFileSha256(file);
      const contentType = file.type || 'application/octet-stream';
      const presign = await createUploadUrl({ fileName: file.name, contentType, contentHash, size: file.size });
      if (!presign.deduped) {
        await uploadFileToPresignedUrl(presign.uploadUrl, file, contentType,
          { 'data-plane-asset-id': presign.assetId, 'data-plane-content-hash': contentHash });
      }
      // context for EXACTLY the agents that need it (room-wide when the need names none)
      const mounts = (need.forAgents && need.forAgents.length)
        ? need.forAgents.map((a) => ({ scope: 'AGENT_POOL', networkId, poolId, agentName: a }))
        : [{ scope: 'POOL', networkId, poolId }];
      await createAsset({ fileName: file.name, contentHash: contentHash || presign.contentHash, size: file.size,
        contentType, usageType: 'REFERENCE', s3Key: presign.s3Key, mounts });
      await send({ action: 'provide', uploadKey: `context:${file.name}` });
    } catch (e) {
      setErr((e && e.message) || 'upload failed');
      setBusy(false);
    }
  }, [need.forAgents, networkId, poolId, send]);

  const blocking = need.blocking && !answered;
  return (
    <div className="rounded border border-slate-200 bg-white px-2.5 py-2 text-[11px] text-slate-700" data-testid={`gap-need-${need.needId}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip tone={need.status === 'failing' ? 'rose' : 'slate'}>{KIND_LABEL[need.kind] || need.kind}</Chip>
        {blocking ? <Chip tone="rose">{need.blocksTasks ? `blocks ${need.blocksTasks} task${need.blocksTasks === 1 ? '' : 's'}` : 'essential'}</Chip> : null}
        {answered ? <Chip tone={need.status === 'not_needed' ? 'slate' : 'emerald'}>{need.status === 'not_needed' ? 'not needed' : 'provided'}</Chip> : null}
        <span className="font-medium text-slate-800">{need.title}</span>
        <span className="ml-auto text-[10px] text-slate-400">
          {need.source === 'prereq_agent' ? 'asked by the gap agent' : need.source === 'rule' ? 'found in the plan'
            : need.source && need.source.startsWith('run:') ? `asked by ${need.source.slice(4)}` : need.source}
        </span>
      </div>
      {need.why ? <div className="mt-0.5 text-slate-500">{need.why}</div> : null}
      {need.status === 'failing' && need.lastError ? <div className="mt-0.5 text-rose-600">Last error: {need.lastError}</div> : null}
      {(need.forAgents || []).length || (need.forTasks || []).length ? (
        <div className="mt-0.5 text-[10px] text-slate-400">
          for {(need.forAgents || []).join(', ')}{(need.forTasks || []).length ? ` · ${(need.forTasks || []).slice(0, 6).join(', ')}${need.forTasks.length > 6 ? '…' : ''}` : ''}
        </div>
      ) : null}
      {answered ? (
        <div className="mt-1 flex items-center gap-2">
          <span className="text-slate-600" data-testid="gap-answer">{answerText(need)}</span>
          <button type="button" className={`${btn} text-slate-600 hover:bg-slate-100`}
            onClick={() => (need.status === 'not_needed' ? send({ action: 'reopen' }) : setMode(mode ? '' : 'provide'))}>
            {need.status === 'not_needed' ? 'Reopen' : (mode ? 'Cancel' : 'Change')}
          </button>
        </div>
      ) : null}
      {mode === 'provide' ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {need.provideVia === 'connector' ? (
            <>
              <select data-testid="gap-connection" value={conn} onChange={(e) => setConn(e.target.value)}
                className="max-w-[18rem] rounded border border-slate-300 px-1.5 py-1 text-[11px]">
                <option value="">{conns === null ? 'Loading connections…' : 'Choose a connection…'}</option>
                {/* The TOOL is the identity (a connector's instanceId is always "default"): it's what the agents
                    are told to use and what a connection Test result is matched on. */}
                {(conns || []).filter((c) => c && c.toolId).map((c) => (
                  <option key={`${c.kind}:${c.toolId}:${c.scope}:${c.instanceId}`} value={c.toolId}>
                    {c.label || c.toolId}{c.scope ? ` · ${String(c.scope).toLowerCase()}` : ''}{c.status ? ` · ${c.status}` : ''}
                  </option>
                ))}
              </select>
              <button type="button" disabled={busy || !conn} data-testid="gap-save"
                className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`} onClick={() => send({ action: 'provide', connectorRef: conn })}>
                Use this connection
              </button>
              {onOpenConnections ? (
                <button type="button" className={`${btn} text-indigo-700 hover:bg-indigo-50`}
                  onClick={() => onOpenConnections(connectorToolFor(need))}
                  data-testid="gap-new-connection"
                  title={connectorToolFor(need)
                    ? `Opens the ${connectorToolFor(need)} setup in Connections — save and test it, then come back here (it's pre-selected)`
                    : 'Set it up in Connections, then come back and choose it here'}>
                  Set up a new connection
                </button>
              ) : null}
            </>
          ) : need.provideVia === 'upload' ? (
            <input type="file" data-testid="gap-file" disabled={busy} onChange={(e) => upload(e.target.files && e.target.files[0])}
              className="text-[11px]" />
          ) : (
            <>
              <input data-testid="gap-value" value={value} onChange={(e) => setValue(e.target.value)}
                placeholder={field.example || FORMAT_HINT[field.format] || field.label || 'Your answer'}
                aria-label={field.label || need.title}
                className="w-72 rounded border border-slate-300 px-1.5 py-1 text-[11px]" />
              <button type="button" disabled={busy || !value.trim()} data-testid="gap-save"
                className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`} onClick={() => send({ action: 'provide', value })}>
                Save
              </button>
            </>
          )}
          {!answered ? (
            <button type="button" className={`${btn} text-slate-500 hover:bg-slate-100`} data-testid="gap-not-needed"
              onClick={() => setMode('not_needed')}>
              Not needed
            </button>
          ) : null}
        </div>
      ) : null}
      {mode === 'not_needed' ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <input data-testid="gap-reason" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Why isn't it needed?" className="w-72 rounded border border-slate-300 px-1.5 py-1 text-[11px]" />
          <button type="button" disabled={busy || reason.trim().length < 3} data-testid="gap-confirm-not-needed"
            className={`${btn} bg-slate-700 text-white hover:bg-slate-800`} onClick={() => send({ action: 'not_needed', reason })}>
            Confirm
          </button>
          <button type="button" className={`${btn} text-slate-500 hover:bg-slate-100`} onClick={() => setMode('provide')}>Back</button>
        </div>
      ) : null}
      {err ? <div className="mt-1 text-rose-600" data-testid="gap-error">{err}</div> : null}
    </div>
  );
}

function Group({ title, hint, items, testid, children }) {
  if (!items && !children) return null;
  return (
    <section className="space-y-1.5" data-testid={testid}>
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {title}{hint ? <span className="ml-1.5 font-normal normal-case tracking-normal text-slate-400">{hint}</span> : null}
      </h4>
      {children}
    </section>
  );
}

export default function GapsView({ networkId, poolId, needs = [], planFixes = null, planFixCount = 0,
  onChanged = null, onOpenConnections = null }) {
  const [showAnswered, setShowAnswered] = useState(false);
  const [showLater, setShowLater] = useState(false);
  const open = needs.filter((n) => n.status === 'open' || n.status === 'failing');
  const broken = open.filter((n) => n.status === 'failing' || n.kind === 'broken_connection');
  const questions = open.filter((n) => n.kind === 'question' && !broken.includes(n));
  const provide = open.filter((n) => !broken.includes(n) && !questions.includes(n))
    .sort((a, b) => Number(b.blocking) - Number(a.blocking));
  const answered = needs.filter((n) => n.status === 'provided' || n.status === 'not_needed');
  const blocking = open.filter((n) => n.blocking).length;
  const rowProps = { networkId, poolId, onChanged, onOpenConnections };
  if (!open.length && !planFixCount && !answered.length) {
    return (
      <div className="rounded bg-emerald-50/60 px-3 py-2 text-[11px] text-emerald-800" data-testid="gaps-empty">
        Nothing is waiting on you. Run gap analysis to check what the plan needs.
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="gaps-view">
      <div className="text-[11px] text-slate-600" data-testid="gaps-summary">
        {blocking
          ? <><b className="text-rose-700">{blocking}</b> essential item{blocking === 1 ? '' : 's'} — the tasks that need them wait until they're answered; everything else keeps moving.</>
          : 'Nothing essential is missing.'}
        {' '}Your answers are shared with the agents that need them on their next run; secrets stay in connections.
      </div>
      {broken.length ? (
        <Group title="Broken connections" hint="the tasks that need these are paused" testid="gaps-broken">
          {broken.map((n) => <NeedRow key={n.needId} need={n} {...rowProps} />)}
        </Group>
      ) : null}
      {/* PHASED (2026-09-30): what the work in progress needs NOW first; the next milestone's after; the
          rest folded. Work only stops at the tasks a need names — everything else moves on. */}
      {provide.filter((n) => (n.when || 'now') === 'now').length ? (
        <Group title="Needed now" hint="for the work in progress" testid="gaps-needs">
          {provide.filter((n) => (n.when || 'now') === 'now').map((n) => <NeedRow key={n.needId} need={n} {...rowProps} />)}
        </Group>
      ) : null}
      {provide.filter((n) => n.when === 'next').length ? (
        <Group title="Coming up" hint="the next milestone" testid="gaps-needs-next">
          {provide.filter((n) => n.when === 'next').map((n) => <NeedRow key={n.needId} need={n} {...rowProps} />)}
        </Group>
      ) : null}
      {provide.filter((n) => n.when === 'later').length ? (
        <section data-testid="gaps-needs-later">
          <button type="button" onClick={() => setShowLater((v) => !v)} className="text-[11px] font-medium text-slate-500 hover:underline">
            {showLater ? '▾' : '▸'} Later ({provide.filter((n) => n.when === 'later').length})
          </button>
          {showLater ? (
            <div className="mt-1.5 space-y-1.5">
              {provide.filter((n) => n.when === 'later').map((n) => <NeedRow key={n.needId} need={n} {...rowProps} />)}
            </div>
          ) : null}
        </section>
      ) : null}
      {planFixCount ? <Group title="Plan fixes" hint="suggested by the gap agent" testid="gaps-plan-fixes">{planFixes}</Group> : null}
      {questions.length ? (
        <Group title="Agent questions" hint="asked during runs" testid="gaps-questions">
          {questions.map((n) => <NeedRow key={n.needId} need={n} {...rowProps} />)}
        </Group>
      ) : null}
      {answered.length ? (
        <section data-testid="gaps-answered">
          <button type="button" onClick={() => setShowAnswered((v) => !v)} className="text-[11px] font-medium text-slate-500 hover:underline">
            {showAnswered ? '▾' : '▸'} Provided ({answered.length})
          </button>
          {showAnswered ? (
            <div className="mt-1.5 space-y-1.5">
              {answered.map((n) => <NeedRow key={n.needId} need={n} answered {...rowProps} />)}
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
